import { getValueBindingComponent } from '@common/utils/component';
import React, { FC, useCallback, useMemo } from 'react';
import { Linking, Platform, View } from 'react-native';
import { WebView } from 'react-native-webview';
import createStyles from './style';

const DEFAULT_REFERER_ORIGIN = 'com.click.app';
const YOUTUBE_EMBED_BASE_URL = 'https://www.youtube.com/embed/';

const Youtube: FC = (attributes: any) => {
  const styles = createStyles(attributes);

  const videoIdPlay = useMemo(() => {
    const url = getValueBindingComponent(attributes, 'url');

    if (url) {
      const regeExURL =
        /^((?:https?:)?\/\/)?((?:www|m)\.)?((?:youtube(-nocookie)?\.com|youtu.be))(\/(?:[\w\-]+\?v=|embed\/|v\/)?)([\w\-]+)(\S+)?$/;
      const matchForUrl = url.match(regeExURL);
      const regExID =
        /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
      const matchForID = url.match(regExID);
      return matchForUrl && matchForID && matchForID[7].length === 11
        ? matchForID[7]
        : '';
    }
  }, [attributes]);

  if (Platform.OS === 'web') {
    return (
      <View style={styles.container}>
        <iframe
          title="PMU"
          src={`https://www.youtube.com/embed/${videoIdPlay}?autoplay=${
            attributes.isAutoPlay ? 1 : 0
          }&origin=${origin}`}
          frameBorder="0"
          allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          style={{
            borderRadius: attributes.borderRadius,
            height: '100%',
            width: '100%',
          }}
        />
      </View>
    );
  }

  const embedUrl = useMemo(() => {
    return `${YOUTUBE_EMBED_BASE_URL}${videoIdPlay}?autoplay=${
      attributes.isAutoPlay ? 1 : 0
    }&showinfo=0&controls=1&fullscreen=1&origin=${encodeURIComponent(
      DEFAULT_REFERER_ORIGIN
    )}`;
  }, [videoIdPlay]);

  // Handle click on "Youtube" button, without this the link to youtube video would still be opened inside the webview
  const handleShouldStartLoadWithRequest = useCallback(
    (event: { url: string }) => {
      const { url } = event;

      if (!url) {
        return true;
      }

      if (
        url.startsWith(YOUTUBE_EMBED_BASE_URL) ||
        url.startsWith('https://www.youtube-nocookie.com/embed/')
      ) {
        return true;
      }

      Linking.openURL(url).catch(() => undefined);

      return false;
    },
    []
  );

  return (
    <View style={styles.container}>
      <WebView
        javaScriptEnabled={true}
        scrollEnabled={false}
        allowsFullscreenVideo={true}
        allowsInlineMediaPlayback={true}
        showsHorizontalScrollIndicator={false}
        showsVerticalScrollIndicator={false}
        mediaPlaybackRequiresUserAction={
          Platform.OS !== 'android' || Platform.Version >= 17
            ? false
            : undefined
        }
        userAgent="Mozilla/5.0 (iPhone; CPU iPhone OS 10_3_1 like Mac OS X) AppleWebKit/603.1.30 (KHTML, like Gecko) Version/10.0 Mobile/14E304 Safari/602.1"
        onShouldStartLoadWithRequest={handleShouldStartLoadWithRequest}
        source={
          Platform.OS === 'android' //Bug in RNWebview andoid, does not allow referer header : https://github.com/react-native-webview/react-native-webview/issues/460
            ? {
                html: createYoutubeHtml(embedUrl),
                baseUrl: DEFAULT_REFERER_ORIGIN,
              }
            : {
                uri: embedUrl,
                headers: {
                  Referer: DEFAULT_REFERER_ORIGIN,
                },
              }
        }
        style={{
          width: attributes.width,
          height: attributes.height,
        }}
      />
    </View>
  );
};

const createYoutubeHtml = (embedUrl: string) => {
  const sanitizedEmbedUrl = embedUrl.replace(/&/g, '&amp;');

  return `<!DOCTYPE html>
<html>
  <head>
    <meta name="referrer" content="origin" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <style>
      html, body {
        margin: 0;
        padding: 0;
        background-color: transparent;
        height: 100%;
      }
      .video-container {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
      }
      iframe {
        width: 100%;
        height: 100%;
        border: 0;
      }
    </style>
  </head>
  <body>
    <div class="video-container">
      <iframe
        src="${sanitizedEmbedUrl}"
        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
        allowfullscreen
      ></iframe>
    </div>
  </body>
</html>`;
};

export default Youtube;
